import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { resolveGalleryAssetUrl } from '../api/httpClientGalleries';
import {
  addImagesToGallery,
  createEntityGallery,
  fetchGalleryById,
  fetchGalleriesByRelatedEntity,
  fetchGalleryByRelatedEntityNews,
  removeImageFromGallery,
  reorderGalleryImages,
  updateGallery,
} from '../controllers/allGalleryController';
import { GALLERY_ENTITY_TYPE, MAX_GALLERY_IMAGES } from '../constants/galleryEntityTypes';

const SortableThumb = ({ img, onRemove, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: img.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: disabled ? 'default' : 'grab',
  };
  return (
    <Grid item xs={6} sm={4} md={3} ref={setNodeRef} style={style} {...attributes} {...(disabled ? {} : listeners)}>
      <Box sx={{ position: 'relative', border: '1px solid #e6e6e6', borderRadius: '8px', overflow: 'hidden' }}>
        <Box
          component="img"
          src={resolveGalleryAssetUrl(img.thumbnailUrl || img.url)}
          alt={img.altText || img.id}
          sx={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
        />
        <IconButton
          size="small"
          disabled={disabled}
          onClick={() => onRemove(img.id)}
          sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: '#FFEEF3', width: 28, height: 28 }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16, color: '#F8285A' }} />
        </IconButton>
      </Box>
    </Grid>
  );
};

const EntityGalleryPanel = ({
  relatedEntityType,
  relatedEntityId,
  relatedEntityStringId = null,
  initialGalleryId = null,
  embeddedGallery = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createTitle, setCreateTitle] = useState('');

  const entityId = relatedEntityId || relatedEntityStringId;

  const loadGallery = useCallback(async () => {
    if (!entityId && !initialGalleryId && !embeddedGallery) {
      setGallery(null);
      setImages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let detail = embeddedGallery || null;
      if (!detail && initialGalleryId) {
        detail = await fetchGalleryById(initialGalleryId);
      }
      if (!detail && relatedEntityType === GALLERY_ENTITY_TYPE.News && relatedEntityStringId) {
        detail = await fetchGalleryByRelatedEntityNews(relatedEntityStringId);
      }
      if (!detail && relatedEntityId) {
        const list = await fetchGalleriesByRelatedEntity(relatedEntityType, relatedEntityId);
        const first = Array.isArray(list) && list.length > 0 ? list[0] : null;
        if (first?.id) {
          detail = Array.isArray(first.images) ? first : await fetchGalleryById(first.id);
        }
      }
      setGallery(detail || null);
      const arr = Array.isArray(detail?.images) ? [...detail.images] : [];
      arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setImages(arr);
    } catch {
      setGallery(null);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [embeddedGallery, entityId, initialGalleryId, relatedEntityId, relatedEntityStringId, relatedEntityType]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const handleCreateGallery = async () => {
    if (!entityId) return;
    setCreating(true);
    try {
      const payload = {
        relatedEntityType,
        title: createTitle.trim() || null,
        showOnDetailPage: true,
        showInGalleryPool: true,
        createdById: '00000000-0000-0000-0000-000000000000',
      };
      if (relatedEntityType === GALLERY_ENTITY_TYPE.News) {
        payload.relatedEntityStringId = relatedEntityStringId;
      } else {
        payload.relatedEntityId = relatedEntityId;
      }
      await createEntityGallery(payload);
      toast.success('Galeri oluşturuldu');
      setCreateTitle('');
      await loadGallery();
    } catch (e) {
      toast.error(e?.message || 'Galeri oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleAddImages = async (fileList) => {
    if (!gallery?.id) return;
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    if (images.length + picked.length > MAX_GALLERY_IMAGES) {
      toast.error('Bir galeri en fazla 50 görsel içerebilir.');
      return;
    }
    setUploading(true);
    try {
      await addImagesToGallery(gallery.id, picked);
      await loadGallery();
      toast.success('Görseller eklendi');
    } catch (e) {
      toast.error(e?.message || 'Görseller eklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (imageId) => {
    if (!gallery?.id) return;
    try {
      await removeImageFromGallery(gallery.id, imageId);
      await loadGallery();
    } catch (e) {
      toast.error(e?.message || 'Görsel kaldırılamadı');
    }
  };

  const handleDragEnd = async (event) => {
    if (!gallery?.id || uploading) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex((i) => i.id === active.id);
    const newIndex = images.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(images, oldIndex, newIndex);
    setImages(newOrder);
    try {
      await reorderGalleryImages(gallery.id, newOrder.map((x) => x.id));
      await loadGallery();
    } catch (e) {
      toast.error(e?.message || 'Sıralama güncellenemedi');
      await loadGallery();
    }
  };

  const handleFlagChange = async (field, checked) => {
    if (!gallery?.id) return;
    try {
      await updateGallery({
        id: gallery.id,
        title: gallery.title,
        description: gallery.description,
        showOnDetailPage: field === 'showOnDetailPage' ? checked : gallery.showOnDetailPage,
        showInGalleryPool: field === 'showInGalleryPool' ? checked : gallery.showInGalleryPool,
      });
      await loadGallery();
    } catch (e) {
      toast.error(e?.message || 'Galeri güncellenemedi');
    }
  };

  if (loading) {
    return (
      <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', borderRadius: '8px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Card>
    );
  }

  if (!gallery) {
    return (
      <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', borderRadius: '8px' }}>
        <Stack spacing={2}>
          <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>
            Galeri
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
            Bu kayıt için henüz galeri yok.
          </Typography>
          <TextField
            size="small"
            placeholder="Galeri başlığı (opsiyonel)"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={creating || !entityId}
            onClick={handleCreateGallery}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', backgroundColor: '#0E4D94' }}
          >
            Galeri Oluştur
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', borderRadius: '8px' }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>
            Galeri {gallery.title ? `— ${gallery.title}` : ''}
          </Typography>
          <Button component="label" variant="outlined" size="small" disabled={uploading} sx={{ textTransform: 'none' }}>
            Görsel Ekle
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                handleAddImages(e.target.files);
                e.target.value = '';
              }}
            />
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={gallery.showOnDetailPage !== false}
                onChange={(e) => handleFlagChange('showOnDetailPage', e.target.checked)}
              />
            }
            label="Detay sayfasında göster"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={gallery.showInGalleryPool !== false}
                onChange={(e) => handleFlagChange('showInGalleryPool', e.target.checked)}
              />
            }
            label="Galeri havuzunda göster"
          />
        </Stack>
        {images.length === 0 ? (
          <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
            Henüz görsel yok. İlk görsel kapak olarak kullanılır.
          </Typography>
        ) : (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
              <Grid container spacing={1}>
                {images.map((img) => (
                  <SortableThumb key={img.id} img={img} onRemove={handleRemoveImage} disabled={uploading} />
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        )}
      </Stack>
    </Card>
  );
};

export default EntityGalleryPanel;
